import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { pathFor } from '../routes/paths';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled.section`
  padding: 100px 0;
  background: white;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  @media (max-width: 968px) {
    padding: 0 40px;
  }
`;

const Title = styled(motion.h2)`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(22px, 4vw, 32px);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #7a7a7a;
  text-align: center;
  margin-bottom: 56px;
`;

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;
  justify-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const ExperienceCard = styled(motion.figure)`
  margin: 0;
  text-align: center;
`;

const ImageBox = styled(Link)`
  display: block;
  width: min(100%, 340px);
  height: 440px;
  overflow: hidden;
  background: #f5f5f5;
  text-decoration: none;
  border-radius: 2px;
  box-shadow: 0 12px 24px rgba(0,0,0,0.06);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 18px 40px rgba(0,0,0,0.12);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Caption = styled.figcaption`
  margin-top: 14px;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-size: 14px;
  color: #a0a0a0;
`;

const WeddingExperienceSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const { lang } = useLanguage();
  const prefix = lang === 'fr' ? '/fr' : '';

  const experiences = [
    {
      id: 1,
      caption: 'Enter the Experience',
      image: '/images/ENTER THE EXPERIENCE.webp',
      link: '/experience'
    },
    {
      id: 2,
      caption: 'Discover the Galleries',
      image: '/images/DISCOVER THE GALLERIES.webp',
      link: '/portfolio'
    },
    {
      id: 3,
      caption: 'Get In Touch',
      image: '/images/GET IN TOUCH.webp',
      link: '/contact'
    }
  ];

  return (
    <SectionContainer ref={ref}>
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {lang === 'fr' ? "découvrir l'élégance" : 'Step Into Elegance'}
        </Title>
        <ExperienceGrid>
          {experiences.map((exp, index) => (
            <ExperienceCard
              key={exp.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              <ImageBox to={exp.link === '/experience' ? pathFor('experience', lang) : `${prefix}${exp.link || '#'}`}>
                <CardImage src={exp.image} alt={exp.caption} />
              </ImageBox>
              <Caption>
                {lang === 'fr'
                  ? (exp.id === 1 ? "Vivre une expérience" : exp.id === 2 ? 'Découvrir les galeries' : 'Me contacter')
                  : exp.caption}
              </Caption>
            </ExperienceCard>
          ))}
        </ExperienceGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default WeddingExperienceSection;
